import React, { useState } from 'react';
import { useUpdatePolicyMutation } from '../../store/api/localApi';
import type { PolicyAudience } from '../../types';
import PolicyAudienceControl from './PolicyAudienceControl';
import { CLASS_NOT_AT_LOCATION_MESSAGE } from './audienceLabel';

interface Props {
  policyId: string;
  locationId: string;
  /** The policy's stored audience, normalised (absent = everyone). */
  current: PolicyAudience;
  onClose: () => void;
}

/**
 * Change who a policy is for without publishing a new document. The server
 * records the change on the policy's review history and asks newly included
 * families; this dialog only picks and saves.
 */
const PolicyAudienceDialog: React.FC<Props> = ({ policyId, locationId, current, onClose }) => {
  const [audience, setAudience] = useState<PolicyAudience | null>(current);
  const [error, setError] = useState<string | null>(null);
  const [updatePolicy, { isLoading }] = useUpdatePolicyMutation();

  const handleSave = async () => {
    if (!audience) return;
    setError(null);
    try {
      await updatePolicy({ policyId, audience }).unwrap();
      onClose();
    } catch (err) {
      const data = (err as { data?: { code?: string; error?: string } })?.data;
      setError(
        data?.code === 'CLASS_NOT_AT_LOCATION'
          ? CLASS_NOT_AT_LOCATION_MESSAGE
          : data?.error || 'Could not save who this policy is for. Try again.'
      );
    }
  };

  return (
    <div
      data-testid="policy-audience-dialog"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="policy-audience-dialog-title"
    >
      <div className="w-full max-w-md rounded-lg bg-white p-5 shadow-xl">
        <h2 id="policy-audience-dialog-title" className="text-lg font-semibold text-gray-900">
          Who is this policy for?
        </h2>
        <div className="mt-4">
          <PolicyAudienceControl
            locationId={locationId}
            value={audience}
            onChange={setAudience}
            disabled={isLoading}
          />
        </div>
        {error && (
          <p data-testid="policy-audience-error" className="mt-3 text-sm text-red-600">
            {error}
          </p>
        )}
        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            data-testid="policy-audience-cancel"
            onClick={onClose}
            className="rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="button"
            data-testid="policy-audience-save"
            disabled={!audience || isLoading}
            onClick={handleSave}
            className="rounded-md bg-primary-600 px-3 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50"
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
};

export default PolicyAudienceDialog;
